import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, CircularProgress, Box, Grid, TextField, Typography } from "@mui/material";
import type { Subscription, ServerLicenseDto, CloudSubscriptionDto } from "client/api-v2";
import { ProductType, addToNewSubscriptionGroup } from "client/api/SubscriptionGroupApi";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { SubmittableFormLayout } from "components/form/SubmittableFormLayout";
import {
  getServerLicenseBySubscriptionId,
  getCloudSubscriptionBySubscriptionId,
} from "../services/subscriptionService";

type AddSubscriptionGroupInput = {
  subscriptionGroupName: string;
};

/**
 * Add New Subscription Group form for Subscriptions.
 * This component determines the platform type (Server or Cloud),
 * fetches the platform entity ID, and creates a subscription group.
 */
export function SubscriptionAddNewSubscriptionGroup() {
  const { entity: subscription, refresh: refreshSubscription } = useEntity<Subscription>();
  const navigate = useNavigate();
  const [platformEntityId, setPlatformEntityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isServerPlatform = subscription?.plan?.productPlatform === "OctopusServer";
  const isCloudPlatform = subscription?.plan?.productPlatform === "OctopusCloud";

  useEffect(() => {
    if (!subscription?.id) {
      setLoading(false);
      setError("Subscription not found");
      return;
    }

    setLoading(true);
    setError(null);

    if (isServerPlatform) {
      getServerLicenseBySubscriptionId(subscription.id)
        .then((license: ServerLicenseDto) => {
          setPlatformEntityId(license.id);
        })
        .catch((err) => {
          console.error("Failed to fetch server license:", err);
          setError("Failed to load server license information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isCloudPlatform) {
      getCloudSubscriptionBySubscriptionId(subscription.id)
        .then((cloudSub: CloudSubscriptionDto) => {
          setPlatformEntityId(cloudSub.id);
        })
        .catch((err) => {
          console.error("Failed to fetch cloud subscription:", err);
          setError("Failed to load cloud subscription information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      setError("Unsupported subscription platform type");
    }
  }, [subscription?.id, isServerPlatform, isCloudPlatform]);

  const createSubscriptionGroupAction = async (input: AddSubscriptionGroupInput) => {
    if (!platformEntityId) {
      throw new Error("Platform entity ID not available");
    }

    const productType = isServerPlatform ? ProductType.ServerLicense : ProductType.CloudSubscription;
    await addToNewSubscriptionGroup(platformEntityId, input.subscriptionGroupName, productType);

    if (refreshSubscription) {
      refreshSubscription();
    }
    navigate(-1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !platformEntityId) {
    return <Alert severity="error">{error || "Unable to load subscription information"}</Alert>;
  }

  const subscriptionGroupNameOptions = {
    required: {
      value: true,
      message: "Please enter a name",
    },
    maxLength: {
      value: 255,
      message: "Subscription group name is too long.",
    },
  };

  return (
    <SubmittableFormLayout header="Create a Subscription group" submitAction={createSubscriptionGroupAction}>
      {(formController) => {
        const { register, formState } = formController;
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Typography variant="body1">
                You are about to create a new subscription group for this subscription
              </Typography>
            </Grid>
            <Grid size={12}>
              <TextField
                required
                error={!!formState?.errors?.subscriptionGroupName}
                helperText={formState?.errors?.subscriptionGroupName?.message}
                id="subscription-group-name"
                label="Subscription group name"
                fullWidth
                variant="standard"
                {...register("subscriptionGroupName", subscriptionGroupNameOptions)}
              />
            </Grid>
          </Grid>
        );
      }}
    </SubmittableFormLayout>
  );
}
